import { useEffect, useRef, useState } from 'react';
import { getRiskIndicatorsData, getRegulatoryRiskHits } from '../../store/investor/apiHelpers';
import { getRiskDotColor, resolveEntityQueryKey } from './riskIndicatorUtils';
import { useParams } from 'react-router-dom';
import { downloadFile, getPresignedUrl } from '../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { useBoundedPoll } from '../../common/hooks/useBoundedPoll';
import { useGraceWindow } from '../../common/hooks/useGraceWindow';
import { useLatestRequestGuard } from '../../common/hooks/useLatestRequestGuard';
import { DATE_FORMATS, formatDateByFormat } from '../../utils/dateUtils';
import { isTerminalFailure } from '../../utils/constants/taskTrackerStatus';
import {
    AICA_GET_TASK_TRACKER_DATA,
    AION_INGEST_AML_PEP_DATA,
    DOWNLOAD_ERROR_MESSAGE,
    GRACE_POLL_ATTEMPTS,
    GRACE_WINDOW_MS,
    RISK_DOT_COLOR_MAP,
    RISK_INDICATOR_STATUS,
    RISK_TYPES,
    TYPES,
    UW_BEFE,
    MAX_POLLING_ATTEMPTS,
    RISK_LABEL_CONFIG,
    RISK_STATE_CONFIG,
    RegulatoryRiskState,
    WRAPPING_TOOLTIP_CSS,
} from './RiskIndicatorConstants';
import { LEGAL_CASES_DIRECTION_BY_KEY, LEGAL_CASES_KEYS, hasLegalCasesCountsShape, resolveLegalCasesState } from './legalCasesRiskState';
import { toast } from 'react-toastify';
import { companyBlack, userBlack } from '../../assets/hostedassets';
import greenTick from '../../assets/green-tick.svg';
import styles from './RiskIndicators.module.scss';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setAmlPepRefreshBanner } from '../../store/investor/action';
import { TabModule } from '../../utils/constants/commonConstants';
import type { Row, RegulatoryRiskDrawerState, RegulatoryRiskHitsResult, ViewerState } from './RiskIndicators';
import { useRegulatoryRiskDrawerConfig } from './useRegulatoryRiskDrawerConfig';
import PotentialRiskRedirectIcon from './PotentialRiskRedirectIcon';
import NotCheckedIcon from './NotCheckedIcon';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';

type PollingPhase = 'pending' | 'grace';

export const useRiskIndicatorStates = ({ rows, setRows, autoFetch }) => {
    const dispatch = useAppDispatch();
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);

    // Read fresh off every fetch tick (v1 tracker shape is a bare string) — pending -> COMPLETE
    // mid-page is the normal case here, unlike LegalCases' one-shot taskTrackerMap snapshot.
    // Stamped with the org it came from — see `taskTrackerStatus` below for why.
    const [tracker, setTracker] = useState<{ orgId: string; status?: string }>({ orgId: '' });
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
    // Set once the pending poll's own attempt budget is spent without ever reaching COMPLETE —
    // the loader must not spin forever on a step that never resolves.
    const [pendingCapReached, setPendingCapReached] = useState(false);
    const [regulatoryRiskDrawerState, setRegulatoryRiskDrawerState] = useState<RegulatoryRiskDrawerState | null>(null);
    const [regulatoryRiskHits, setRegulatoryRiskHits] = useState<RegulatoryRiskHitsResult | null>(null);
    const [regulatoryRiskHitsLoading, setRegulatoryRiskHitsLoading] = useState(false);
    const params = useParams();
    const { config: regulatoryRiskDrawerConfig, ensureConfigLoaded } = useRegulatoryRiskDrawerConfig();

    // Assigned at render time (not in an effect) so it already reflects the new org on the very
    // commit params.id changes — an effect-based assignment would land one render late, which is
    // exactly the gap fetchRiskIndicators' org check below needs closed.
    const activeOrgIdRef = useRef('');
    activeOrgIdRef.current = params?.id ?? '';

    // Prefetch config on mount so the first drawer open already has title/chip labels
    // resolved, instead of showing raw checkType until ensureConfigLoaded resolves.
    useEffect(() => {
        ensureConfigLoaded(investorOrgId);
    }, [investorOrgId, ensureConfigLoaded]);

    const handleTaskTrackerData = (taskTrackerData, requestOrgId: string) => {
        if (!taskTrackerData) return;
        setTracker({ orgId: requestOrgId, status: taskTrackerData?.taskTrackerData?.[AION_INGEST_AML_PEP_DATA] });
    };

    const processUwBefeData = (uwBefeData, requestOrgId: string) => {
        dispatch(setAmlPepRefreshBanner({ orgId: requestOrgId, show: uwBefeData?.showRefreshBanner === true }));
        if (uwBefeData && Object.keys(uwBefeData).length > 0) {
            const formattedLastUpdatedAt = formatDateByFormat(uwBefeData.lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);

            setRows(uwBefeData?.data || []);
            setLastUpdatedAt(formattedLastUpdatedAt || null);
        } else {
            setRows([]);
            setLastUpdatedAt(null);
        }
    };

    // Stale-response guard (mirrors onOpenRegulatoryRisk below): the token covers same-org
    // supersession — it is bumped by every new request, including one fired for the same org, so
    // a same-org response can only be current if no later request for that org has since started.
    // It does NOT cover the route-change gap: params.id can flip to a new org before that org's
    // own mount effect has started its request, since a resolved promise's continuation is a
    // microtask that can run before React's passive effects flush — so a stale org's response can
    // still read as "current" by token alone. The activeOrgIdRef check below covers that gap.
    const riskIndicatorsRequestGuard = useLatestRequestGuard();

    const fetchRiskIndicators = async () => {
        const token = riskIndicatorsRequestGuard.startRequest();
        const requestOrgId = params?.id ?? '';
        const payload = {
            orgId: requestOrgId,
        };
        const response = await getRiskIndicatorsData(payload);

        if (!riskIndicatorsRequestGuard.isCurrent(token) || requestOrgId !== activeOrgIdRef.current) return;

        if (response) {
            handleTaskTrackerData(response?.responseBody?.[AICA_GET_TASK_TRACKER_DATA], requestOrgId);
            processUwBefeData(response?.responseBody?.[UW_BEFE], requestOrgId);
        }
    };

    useEffect(() => {
        if (params?.id && autoFetch) {
            fetchRiskIndicators();
        }
    }, [params.id, autoFetch]);

    useEffect(() => {
        if (autoFetch && modulePollingList.includes(TabModule.LEGAL_CHECKS)) {
            fetchRiskIndicators();
        }
    }, [modulePollingList, autoFetch]);

    // Resets the pending-cap flag on a genuine org switch — a stale cap state from the previous
    // org must not leak into the next one. The tracker deliberately has no reset effect here
    // (see `taskTrackerStatus` below). The grace window is reset internally by useGraceWindow,
    // keyed on the same params.id.
    useEffect(() => {
        setPendingCapReached(false);
    }, [params.id]);

    // Derived at render time, not reset in an effect: on the commit where params.id changes the
    // tracker still belongs to the previous org, and an effect-based reset would land one render
    // late — useGraceWindow would already have armed the new org's window off the old org's
    // COMPLETE. Reading undefined until the new org's OWN fetch resolves closes that gap.
    const taskTrackerStatus = tracker.orgId === (params?.id ?? '') ? tracker.status : undefined;
    const isTerminal = isTerminalFailure(taskTrackerStatus);
    const isComplete = taskTrackerStatus === RISK_INDICATOR_STATUS.COMPLETE;
    const phase: PollingPhase = isComplete ? 'grace' : 'pending';

    // Arms the window exactly once per params.id — the tracker is read fresh every tick, so this
    // fires on the first COMPLETE observed and ignores every COMPLETE read after. Only the
    // autoFetch owner may arm it: arming is gated on autoFetch because only the owner instance
    // ever fetches at all — a non-owner never gets a tracker update to arm off.
    const { windowExpired } = useGraceWindow({
        isComplete,
        resetKey: params.id ?? '',
        windowMs: GRACE_WINDOW_MS,
        enabled: autoFetch,
        onArm: () => setPendingCapReached(false),
    });

    // Only the autoFetch owner (RiskIndicators.tsx) may poll — Rows/Card instances mount with
    // their own taskTrackerStatus that would otherwise start a second interval on the same tracker.
    const shouldPoll = autoFetch && !isTerminal && (!isComplete || !windowExpired);

    useBoundedPoll(fetchRiskIndicators, shouldPoll, () => setPendingCapReached(true), {
        intervalMs: 10000,
        maxAttempts: phase === 'pending' ? MAX_POLLING_ATTEMPTS : GRACE_POLL_ATTEMPTS,
        fireImmediately: false,
        stickyAttemptsKey: `${params.id}:${phase}`,
    });

    // Stays up while pending, and inside the grace window until rows actually land. Before the BE
    // fix ships a step can sit pending forever — pendingCapReached is what eventually clears this
    // instead of spinning forever; the grace window elapsing (polling stopping) no longer forces
    // this false on its own.
    const activeInLadder = !isTerminal && !pendingCapReached;
    const loading = activeInLadder && (!isComplete || (!windowExpired && rows.length === 0));

    // Header indicator: "is the loop still running", independent of whether rows have already
    // landed — unlike `loading`, this stays up through the grace window even once rows are on
    // screen, so a refresh in flight is never invisible. `loading` is kept as-is for other
    // consumers.
    const isPolling = activeInLadder && (!isComplete || !windowExpired);

    // Header indicator's failure state — the counterpart to isPolling above. isTerminal already
    // excludes it from isPolling/activeInLadder, so the two flags can never both be true. With
    // last-known rows already on screen the header goes quiet instead of showing the indicator —
    // only a terminal failure with nothing to show earns the "Not able to fetch" treatment.
    const isFetchFailed = isTerminal && rows.length === 0;

    // Header indicator's status text, next to the spinner. Rendered regardless of whether rows
    // are already on screen, so a refresh in flight is never invisible (mirrors LegalCases).
    // lastUpdatedLabel is a separate, independent signal — rendered alongside statusMessage
    // rather than instead of it, so both are visible together during the post-COMPLETE grace poll.
    let statusMessage = '';
    if (isPolling) {
        statusMessage = 'Processing (may take ~5 mins)';
    } else if (!isFetchFailed && rows.length === 0) {
        // isFetchFailed is deliberately excluded here — the failure indicator carries its own
        // message, and "No Data Available" must not also render alongside it.
        statusMessage = 'No Data Available';
    }

    const lastUpdatedLabel = rows.length > 0 && lastUpdatedAt ? `Last Updated on ${lastUpdatedAt}` : null;

    // Stale-response guard (mirrors riskIndicatorsRequestGuard above): a fast
    // pill A -> B switch can let A's late response land after B's request started.
    // Only commit the result if this request is still the most recent one opened.
    const regulatoryRiskRequestGuard = useLatestRequestGuard();

    const onOpenRegulatoryRisk = async (row: Row, checkType: string) => {
        const token = regulatoryRiskRequestGuard.startRequest();
        setRegulatoryRiskDrawerState({ row, checkType });
        setRegulatoryRiskHits(null);
        setRegulatoryRiskHitsLoading(true);
        // getRegulatoryRiskHits never rejects (it catches internally and resolves null),
        // so this single post-await branch covers both the success and error cases.
        // resolveEntityQueryKey handles the null-identifier sentinel fallback — see its doc.
        const result = await getRegulatoryRiskHits({
            orgId: params?.id ?? '',
            ...resolveEntityQueryKey(row),
            checkType,
        });
        if (regulatoryRiskRequestGuard.isCurrent(token)) {
            setRegulatoryRiskHits(result);
            setRegulatoryRiskHitsLoading(false);
        }
    };

    const closeRegulatoryRiskDrawer = () => {
        setRegulatoryRiskDrawerState(null);
        setRegulatoryRiskHits(null);
        setRegulatoryRiskHitsLoading(false);
    };

    const getRowImage = (type) => {
        switch (type) {
            case TYPES.ORGANIZATION:
                return <img src={companyBlack} alt="company" style={{ width: '16px', height: '16px', marginTop: '2px' }} />;
            case TYPES.INDIVIDUAL:
                return <img src={userBlack} alt="user" style={{ width: '16px', height: '16px', marginTop: '2px' }} />;
            default:
                return null;
        }
    };

    const RiskDotMessage = () => {
        return (
            <div className={styles.RiskDotMessage}>
                {RISK_TYPES.map((risk, index) => (
                    <div key={index} className={styles.RiskIndicators}>
                        <span className={styles.RiskDot} style={{ backgroundColor: risk.color }}></span>
                        <span>{risk.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    /*
        RECUR-27829. Two renderers, deliberately kept apart:

        - getStateIndicator reads the per-cell state the BE derives from the actual hit rows.
          This is the path from now on.
        - getScoreIndicator is the OLD score path, kept verbatim as the rollout fallback.
          polus omits riskState until underwriting-engine ships, and this repo may deploy
          first, so the column must not go blank in that window.

        Openability is keyed on the STATE, not on the label config. It used to be set only in
        the POTENTIAL_RISK branch, so mapping the new CONFIRMED state onto the existing
        HIGH_RISK label would have made the most severe cell the only unclickable one.
    */
    /*
        `pill` distinguishes the two callers, which want different boxes.

        The legacy score path draws a real pill: the cell behind it is untinted, so the label needs
        its own background, padding and radius to read as a chip.

        The RECUR-27829 state path does not. The design tints the whole CELL and puts a plain
        icon+text row inside it (Figma 34293-27364: "Table cell" carries the fill and 16px of
        padding; there is no inner chip). Giving that row its own 10px of padding pushed every
        value 10px right of its own column header — measured: header text at x=848, cell content
        at x=858 — so the column read as misaligned all the way down.
    */
    const renderIndicator = ({
        label,
        // Legal-cases-only, both optional: a second smaller line inside the cell, and a hover
        // tooltip wrapping the whole element. Every existing caller passes neither, so the
        // layout/markup below is unchanged when both are absent.
        subLabel,
        tooltip,
        // Wraps the tooltip bubble instead of forcing nowrap width.
        tooltipWrap = false,
        color,
        backgroundColor,
        openable,
        row,
        checkType,
        onOpen,
        pill = true,
        tick = false,
        notCheckedIcon = false,
    }: {
        label: string | null;
        subLabel?: string;
        tooltip?: string;
        tooltipWrap?: boolean;
        color: string;
        backgroundColor: string;
        openable: boolean;
        row?: Row;
        checkType?: string;
        onOpen?: (row: Row, checkType: string) => void;
        pill?: boolean;
        tick?: boolean;
        notCheckedIcon?: boolean;
    }) => {
        const openDrawer = () => row && checkType && onOpen?.(row, checkType);
        // The tick REPLACES the arrow rather than joining it: No Open is the only state that is
        // both tick and openable, so without the !tick guard it would render both icons.
        const iconAndLabel = (
            <>
                {tick && <img src={greenTick} alt="" className={styles.NoMatchTickIcon} />}
                {notCheckedIcon && <NotCheckedIcon className={styles.NoMatchTickIcon} color={color} />}
                {openable && !tick && <PotentialRiskRedirectIcon className={styles.RiskRedirectIcon} color={color} />}
                {label}
            </>
        );
        const indicator = (
            <span
                role={openable ? 'button' : undefined}
                tabIndex={openable ? 0 : undefined}
                onClick={openable ? openDrawer : undefined}
                onKeyDown={
                    openable
                        ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openDrawer();
                              }
                          }
                        : undefined
                }
                style={{
                    // On the state path the cell owns the tint and the padding; see `pill` above.
                    ...(pill ? { backgroundColor, padding: '5px 10px', borderRadius: '16px' } : null),
                    color,
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: openable ? 'pointer' : 'default',
                }}
                className={subLabel ? styles.RiskIndicatorStacked : styles.RiskIndicatorRow}
            >
                {subLabel ? <span className={styles.RiskIndicatorRow}>{iconAndLabel}</span> : iconAndLabel}
                {subLabel && <span className={styles.RiskSubLabel}>{subLabel}</span>}
            </span>
        );
        // usePortal: the table has overflow + sticky columns (matches
        // ManagementProfileTab/managementProfileColumnRenderers.tsx's Tooltip usage).
        // fitContent/small/contentCss: the shared Tooltip's TIP defaults to a 420px bubble meant
        // for paragraph-length content — for this five-word hint it hugs the text instead, same
        // as KeyOfficialsCreditSummary.tsx's shareholding-value tooltip. Independent of
        // containerFullWidth below: fitContent/small/minWidth style the `.Tooltip-Tip` bubble,
        // containerFullWidth only styles the `.Tooltip-Wrapper` trigger div around `indicator` —
        // verified neither affects the other.
        // containerFullWidth (only when there's a subLabel): the shared Tooltip's own wrapper div
        // is inline-block, which shrink-wraps to content by default — harmless for a short single
        // line, but it's what let a wide disposed-count sub-line force the wrapper (and the table
        // cell around it) wider than the column instead of wrapping inside it. `false` here is the
        // prop's own default, so a cell with no subLabel renders byte-identically to before.
        return tooltip ? (
            <Tooltip
                content={tooltip}
                direction="top"
                usePortal
                fitContent
                small
                focusable={!openable}
                contentCss={tooltipWrap ? WRAPPING_TOOLTIP_CSS : { whiteSpace: 'nowrap' }}
                containerFullWidth={!!subLabel}
            >
                {indicator}
            </Tooltip>
        ) : (
            indicator
        );
    };

    const getScoreIndicator = (risk, row?: Row, checkType?: string, onOpen?: (row: Row, checkType: string) => void) => {
        let config;
        let isPotentialRisk = false;
        switch (true) {
            case risk === 0:
                config = RISK_LABEL_CONFIG.NO_RISK;
                break;
            case risk > 0 && risk <= 100:
                config = RISK_LABEL_CONFIG.POTENTIAL_RISK;
                isPotentialRisk = true;
                break;
            default:
                config = RISK_LABEL_CONFIG.NOT_AVAILABLE;
        }
        return renderIndicator({
            label: config.label,
            color: config.color,
            backgroundColor: config.backgroundColor,
            openable: isPotentialRisk,
            row,
            checkType,
            onOpen,
        });
    };

    const getRiskIndicator = (risk, row?: Row, checkType?: string, onOpen?: (row: Row, checkType: string) => void) => {
        const cell = checkType ? row?.riskState?.[checkType] : undefined;
        if (!cell?.state) return getScoreIndicator(risk, row, checkType, onOpen);

        // Legal Cases (both directions) run their own five-state machine once polus has shipped
        // the split open/disposed counts; a legacy-shape cell falls through to the generic path
        // below unchanged (see hasLegalCasesCountsShape's doc for why that's the safe default).
        if (checkType && LEGAL_CASES_KEYS.has(checkType) && hasLegalCasesCountsShape(cell)) {
            const resolved = resolveLegalCasesState(cell, LEGAL_CASES_DIRECTION_BY_KEY[checkType]);
            return renderIndicator({
                label: resolved.label,
                subLabel: resolved.subLabel,
                tooltip: resolved.tooltip,
                color: resolved.color,
                backgroundColor: resolved.backgroundColor,
                openable: resolved.openable,
                row,
                checkType,
                onOpen,
                pill: false,
                tick: resolved.tick,
            });
        }

        const config = RISK_STATE_CONFIG[cell.state] ?? RISK_STATE_CONFIG[RegulatoryRiskState.NOT_AVAILABLE];
        // Per the ticket: show the confirmed count when there is one, otherwise the probable
        // count. A state that carries no count (No Match / No Data) renders its label alone.
        const count = (cell.confirmedCount ?? 0) > 0 ? cell.confirmedCount : cell.probableCount;
        const label = config.word ? `${count} ${config.word}` : config.label;

        return renderIndicator({
            label,
            tooltip: config.tooltip,
            tooltipWrap: config.tooltipWrap,
            color: config.color,
            backgroundColor: config.backgroundColor,
            openable: config.openable,
            row,
            checkType,
            onOpen,
            pill: false,
            tick: config.tick,
            notCheckedIcon: config.notCheckedIcon,
        });
    };

    const getRiskDot = (risk, state?: string) => {
        const color = getRiskDotColor(risk, state);
        return (
            <span
                style={{
                    backgroundColor: color,
                }}
                className={styles.RiskDot}
            ></span>
        );
    };

    return {
        loading,
        isPolling,
        isFetchFailed,
        hoveredIndex,
        setHoveredIndex,
        lastUpdatedAt,
        statusMessage,
        lastUpdatedLabel,
        getRowImage,
        RiskDotMessage,
        getRiskIndicator,
        getRiskDot,
        regulatoryRiskDrawerState,
        regulatoryRiskHits,
        regulatoryRiskHitsLoading,
        onOpenRegulatoryRisk,
        closeRegulatoryRiskDrawer,
        regulatoryRiskDrawerConfig,
    };
};
